import { Component, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../../core/services/auth.service';
import { DatabaseService } from '../../core/services/database.service';
import { LoggerService } from '../../core/services/logger.service';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';

@Component({
  selector: 'app-dev-sql',
  standalone: true,
  imports: [FormsModule, TranslatePipe, CardComponent, CardContentComponent],
  templateUrl: './dev-sql.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './dev-sql.component.css',
})
export class DevSqlComponent {
  private db = inject(DatabaseService);
  private auth = inject(AuthService);
  private log = inject(LoggerService).getLogger('dev-sql');

  query = signal<string>('SELECT * FROM organization;');
  params = signal<string>('');
  results = signal<any[]>([]);
  error = signal<string | null>(null);
  success = signal<string | null>(null);
  executing = signal(false);

  get customerId(): string {
    const token = this.auth.getToken();
    if (!token) return 'No token found';
    try {
      const payload = this.auth.decodeJwt(token);
      return payload.customer_id || 'No customer_id in token';
    } catch {
      return 'Invalid token';
    }
  }

  get dbName(): string {
    return `cp2-customer_${this.customerId}`;
  }

  async executeQuery() {
    this.executing.set(true);
    this.error.set(null);
    this.success.set(null);
    this.results.set([]);

    try {
      const parsedParams = this.parseParams(this.params());
      this.log.info('Executing:', this.query(), 'Params:', parsedParams);
      const result = await this.db.exec(this.query(), parsedParams);
      this.log.info('Results:', result);
      this.log.debug('Result table:', result);
      this.results.set(result || []);

      if (!result || result.length === 0) {
        this.success.set('✅ Query executed successfully (0 rows returned)');
      }
    } catch (err: any) {
      this.log.error('Error:', err);
      this.error.set(err.message || String(err));
    } finally {
      this.executing.set(false);
    }
  }

  async listTables() {
    this.query.set("SELECT name FROM sqlite_master WHERE type='table' ORDER BY name;");
    await this.executeQuery();
  }

  async showOrganizations() {
    this.query.set('SELECT * FROM organization;');
    await this.executeQuery();
  }

  async showCheckPoints() {
    this.query.set('SELECT * FROM assets;');
    await this.executeQuery();
  }

  async showSyncChanges() {
    this.query.set('SELECT * FROM _sync_log ORDER BY rowid DESC LIMIT 50;');
    await this.executeQuery();
  }

  async showSchemaVersion() {
    this.query.set(
      "SELECT name FROM sqlite_master WHERE type='table' AND name LIKE '_sync%' ORDER BY name;",
    );
    await this.executeQuery();
  }

  getColumns(): string[] {
    const results = this.results();
    if (!results || results.length === 0) return [];
    return Object.keys(results[0]);
  }

  formatValue(value: any): string {
    if (value === null) return 'NULL';
    if (value === undefined) return 'undefined';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  /**
   * Parse params input string into an array.
   * Supports comma-separated values. Empty string = empty array.
   * Values are trimmed. Quoted strings have quotes removed.
   */
  private parseParams(input: string): any[] {
    if (!input.trim()) return [];
    return input.split(',').map((v) => {
      const trimmed = v.trim();
      // Remove surrounding quotes if present
      if (
        (trimmed.startsWith("'") && trimmed.endsWith("'")) ||
        (trimmed.startsWith('"') && trimmed.endsWith('"'))
      ) {
        return trimmed.slice(1, -1);
      }
      // Try to parse as number
      const num = Number(trimmed);
      if (!isNaN(num) && trimmed !== '') return num;
      return trimmed;
    });
  }
}
